/**
 * Blog posts: filters (search, industry, topic) + client-side pagination.
 * Renders all articles in DOM; filters and paginates in JS.
 */
(function () {
  const section = document.querySelector('.blog-posts');
  if (!section || !(section instanceof HTMLElement)) return;

  const countEl = document.getElementById('blog-posts-count');
  const searchInput = /** @type {HTMLInputElement | null} */ (document.getElementById('blog-filters-search'));
  const paginationEl = document.getElementById('blog-posts-pagination');
  const closestSection = section.closest('.section');
  const grid = document.querySelector('.blog-posts-container');
  const items = /** @type {HTMLElement[]} */ (grid ? Array.from(grid.querySelectorAll('.blog-post-item')) : []);
  const filtersToggle = document.getElementById('blog-filters-toggle');
  const filtersOverlay = document.getElementById('blog-filters-overlay');
  const filtersDrawer = document.getElementById('blog-filters-drawer');
  const filtersApplyBtn = document.getElementById('blog-filters-apply');
  const articlesPerPage = parseInt(section.dataset.articlesPerPage || '12', 10);

  function getSearchQuery() {
    return (searchInput && searchInput.value ? searchInput.value.trim() : '').toLowerCase();
  }

  function getSelectedIndustries() {
    const checkboxes = document.querySelectorAll('.blog-filters__checkbox[name="industry"]:checked');
    return Array.from(checkboxes).map(function (cb) { return cb instanceof HTMLInputElement ? cb.value.trim() : ''; }).filter(Boolean);
  }

  function getSelectedTopics() {
    const checkboxes = document.querySelectorAll('.blog-filters__checkbox[name="topic"]:checked');
    return Array.from(checkboxes).map(function (cb) { return cb instanceof HTMLInputElement ? cb.value.trim() : ''; }).filter(Boolean);
  }

  /**
   * @param {HTMLElement} item
   * @param {string} query
   */
  function itemMatchesSearch(item, query) {
    if (!query) return true;
    const text = (item.dataset.searchable || '').toLowerCase();
    return text.indexOf(query) !== -1;
  }

  /**
   * @param {HTMLElement} item
   * @param {string[]} selectedIndustries
   */
  function itemMatchesIndustry(item, selectedIndustries) {
    if (selectedIndustries.length === 0) return true;
    const tagsStr = item.dataset.tags || '';
    const tags = tagsStr.split(',').map(function (t) { return t.trim(); }).filter(Boolean);
    return selectedIndustries.some(function (tag) { return tags.includes(tag); });
  }

  /**
   * @param {HTMLElement} item
   * @param {string[]} selectedTopics
   */
  function itemMatchesTopic(item, selectedTopics) {
    if (selectedTopics.length === 0) return true;
    const topic = (item.dataset.topic || '').trim();
    if (!topic) return false;
    return selectedTopics.includes(topic);
  }

  function getVisibleItems() {
    const query = getSearchQuery();
    const industries = getSelectedIndustries();
    const topics = getSelectedTopics();
    return items.filter(function (item) {
      return itemMatchesSearch(item, query) &&
        itemMatchesIndustry(item, industries) &&
        itemMatchesTopic(item, topics);
    });
  }

  function getPageFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const p = parseInt(params.get('blog_page') || '1', 10);
    return p > 0 ? p : 1;
  }

  /** @param {number} page */
  function setPageInUrl(page) {
    const url = new URL(window.location.href);
    if (page <= 1) {
      url.searchParams.delete('blog_page');
    } else {
      url.searchParams.set('blog_page', String(page));
    }
    window.history.replaceState({}, '', url.toString());
  }

  function scrollToContainer() {
    if (!closestSection) return;
    var headerHeightStr = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
    var headerHeight = headerHeightStr ? parseInt(headerHeightStr, 10) : 60;
    if (isNaN(headerHeight)) headerHeight = 60;
    var top = closestSection.getBoundingClientRect().top + window.scrollY - headerHeight;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function isMobileFilters() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function openDrawer() {
    if (!filtersDrawer || !filtersOverlay || !filtersToggle) return;
    filtersDrawer.classList.add('is-open');
    filtersOverlay.classList.add('is-visible');
    filtersOverlay.setAttribute('aria-hidden', 'false');
    filtersToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    if (!filtersDrawer || !filtersOverlay || !filtersToggle) return;
    filtersDrawer.classList.remove('is-open');
    filtersOverlay.classList.remove('is-visible');
    filtersOverlay.setAttribute('aria-hidden', 'true');
    filtersToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  function applyFilters() {
    const visible = getVisibleItems();
    const totalPages = Math.max(1, Math.ceil(visible.length / articlesPerPage));
    let currentPage = getPageFromUrl();
    if (currentPage > totalPages) currentPage = totalPages;
    if (currentPage < 1) currentPage = 1;
    setPageInUrl(currentPage);

    items.forEach(function (item) {
      const inVisible = visible.indexOf(item) !== -1;
      if (item instanceof HTMLElement) item.style.display = inVisible ? '' : 'none';
      item.classList.remove('blog-post-item--page-hidden');
    });

    const start = (currentPage - 1) * articlesPerPage;
    const end = start + articlesPerPage;
    visible.forEach(function (item, index) {
      const onPage = index >= start && index < end;
      item.classList.toggle('blog-post-item--page-hidden', !onPage);
    });

    if (countEl) {
      countEl.textContent = 'Showing ' + visible.length + ' article' + (visible.length === 1 ? '' : 's');
    }

    renderPagination(visible.length, currentPage, totalPages);
  }

  /**
   * @param {number} totalVisible
   * @param {number} currentPage
   * @param {number} totalPages
   */
  function renderPagination(totalVisible, currentPage, totalPages) {
    if (!paginationEl) return;
    paginationEl.innerHTML = '';
    if (totalPages <= 1 && totalVisible <= articlesPerPage) {
      paginationEl.hidden = true;
      return;
    }
    paginationEl.hidden = false;

    var ul = document.createElement('ul');
    ul.className = 'blog-posts__pagination-list';
    ul.setAttribute('role', 'list');

    var prevLi = document.createElement('li');
    if (currentPage <= 1) {
      var span = document.createElement('span');
      span.className = 'blog-posts__pagination-span blog-posts__pagination-link blog-posts__pagination-link--arrow blog-posts__pagination-span--disabled';
      span.setAttribute('aria-disabled', 'true');
      span.textContent = 'Previous';
      prevLi.appendChild(span);
    } else {
      var a = document.createElement('a');
      a.href = '#';
      a.className = 'blog-posts__pagination-link blog-posts__pagination-link--arrow';
      a.setAttribute('aria-label', 'Previous page');
      a.textContent = 'Previous';
      a.addEventListener('click', function (e) {
        e.preventDefault();
        setPageInUrl(currentPage - 1);
        applyFilters();
        scrollToContainer();
      });
      prevLi.appendChild(a);
    }
    ul.appendChild(prevLi);

    for (var i = 1; i <= totalPages; i++) {
      var li = document.createElement('li');
      if (i === currentPage) {
        var spanCur = document.createElement('span');
        spanCur.className = 'blog-posts__pagination-link blog-posts__pagination-link--active';
        spanCur.setAttribute('aria-current', 'page');
        spanCur.textContent = String(i);
        li.appendChild(spanCur);
      } else {
        var aCur = document.createElement('a');
        aCur.href = '#';
        aCur.className = 'blog-posts__pagination-link';
        aCur.setAttribute('aria-label', 'Page ' + i);
        aCur.textContent = String(i);
        aCur.addEventListener('click', function (e) {
          e.preventDefault();
          setPageInUrl(parseInt(this.textContent, 10));
          applyFilters();
          scrollToContainer();
        });
        li.appendChild(aCur);
      }
      ul.appendChild(li);
    }

    var nextLi = document.createElement('li');
    if (currentPage >= totalPages) {
      var spanNext = document.createElement('span');
      spanNext.className = 'blog-posts__pagination-span blog-posts__pagination-link blog-posts__pagination-link--arrow blog-posts__pagination-span--disabled';
      spanNext.setAttribute('aria-disabled', 'true');
      spanNext.textContent = 'Next';
      nextLi.appendChild(spanNext);
    } else {
      var aNext = document.createElement('a');
      aNext.href = '#';
      aNext.className = 'blog-posts__pagination-link blog-posts__pagination-link--arrow';
      aNext.setAttribute('aria-label', 'Next page');
      aNext.textContent = 'Next';
      aNext.addEventListener('click', function (e) {
        e.preventDefault();
        setPageInUrl(currentPage + 1);
        applyFilters();
        scrollToContainer();
      });
      nextLi.appendChild(aNext);
    }
    ul.appendChild(nextLi);

    paginationEl.appendChild(ul);
  }

  function attachListeners() {
    if (searchInput) {
      searchInput.addEventListener('input', function () {
        setPageInUrl(1);
        applyFilters();
      });
      searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          if (isMobileFilters() && filtersDrawer && filtersDrawer.classList.contains('is-open')) {
            closeDrawer();
          } else {
            if (searchInput) searchInput.value = '';
            applyFilters();
            if (searchInput) searchInput.blur();
          }
        }
      });
    }

    document.querySelectorAll('.blog-filters__checkbox').forEach(function (cb) {
      cb.addEventListener('change', function () {
        setPageInUrl(1);
        applyFilters();
      });
    });

    if (filtersToggle) {
      filtersToggle.addEventListener('click', function () {
        if (isMobileFilters()) openDrawer();
      });
    }
    if (filtersOverlay) {
      filtersOverlay.addEventListener('click', closeDrawer);
    }
    if (filtersDrawer) {
      var closeBtn = filtersDrawer.querySelector('.blog-filters-drawer__close');
      if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    }
    if (filtersApplyBtn) {
      filtersApplyBtn.addEventListener('click', function () {
        applyFilters();
        closeDrawer();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && filtersDrawer && filtersDrawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });
    window.addEventListener('resize', function () {
      if (!isMobileFilters() && filtersDrawer && filtersDrawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });
  }

  if (items.length > 0) {
    attachListeners();
    applyFilters();
  }
})();
